/**
 * Utility functions for evaluation dashboard
 */

/**
 * Extract technical metadata from case item, checking multiple possible locations
 */
export const extractTechnicalMetadata = (caseItem) => {
  return {
    response_time_ms: 
      caseItem.response_time_ms || 
      caseItem.evaluation?.response_time_ms || 
      caseItem.metadata?.response_time_ms || 
      null,
    judge_model: 
      caseItem.judge_model || 
      caseItem.evaluation?.judge_model || 
      caseItem.metadata?.judge_model || 
      caseItem.evaluation?.modelUsed || 
      null,
    evaluated_at: 
      caseItem.evaluated_at || 
      caseItem.evaluation?.evaluated_at || 
      caseItem.metadata?.evaluated_at || 
      caseItem.timestamp || 
      null,
  };
};

/**
 * Extract case metadata from case item
 */
export const extractCaseMetadata = (caseItem) => {
  return {
    category: 
      caseItem.category || 
      caseItem.case_metadata?.category || 
      caseItem.metadata?.category || 
      '',
    expected_quality: 
      caseItem.expected_quality || 
      caseItem.case_metadata?.expected_quality || 
      caseItem.metadata?.expected_quality || 
      undefined,
  };
};

/**
 * Get target message index and normalized history
 * Handles both forward and reverse chronological order
 */
export const getTargetMessageInfo = (history, respondedToIndex) => {
  if (!history || !Array.isArray(history) || history.length === 0) {
    return { targetIndex: -1, normalizedHistory: null };
  }

  let targetMessageIndex = -1;
  
  if (respondedToIndex !== undefined && respondedToIndex !== null) {
    targetMessageIndex = respondedToIndex;
  } else {
    // Find last user message
    for (let i = history.length - 1; i >= 0; i--) {
      if (history[i].role === 'user') {
        targetMessageIndex = i;
        break;
      }
    }
  }

  if (targetMessageIndex < 0) {
    return { targetIndex: -1, normalizedHistory: null };
  }

  // Determine if history is in reverse chronological order
  let normalizedHistory = history;
  
  if (targetMessageIndex >= 0 && targetMessageIndex < 2 && history.length > 2) {
    normalizedHistory = [...history].reverse();
    targetMessageIndex = normalizedHistory.length - 1 - targetMessageIndex;
  }

  return { targetIndex: targetMessageIndex, normalizedHistory };
};

/**
 * Get preview text with max length
 */
export const getPreviewText = (text, maxLength = 150) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Format date safely
 */
export const formatDate = (dateString) => {
  if (!dateString) return null;
  try {
    return new Date(dateString).toLocaleString();
  } catch (e) {
    console.warn('Invalid date string:', dateString);
    return dateString;
  }
};

/**
 * Format conversation history as plain text for copying
 */
export const formatConversationHistoryForCopy = (history, respondedToIndex) => {
  if (!history || !Array.isArray(history) || history.length === 0) {
    return '';
  }

  const { targetIndex, normalizedHistory } = getTargetMessageInfo(history, respondedToIndex);
  
  if (targetIndex < 0 || !normalizedHistory) {
    return '';
  }

  let messagesToShow = normalizedHistory;
  let displayTargetIndex = targetIndex;
  
  if (targetIndex >= 0 && targetIndex < 2 && history.length > 2) {
    messagesToShow = [...history].reverse();
    displayTargetIndex = messagesToShow.length - 1 - targetIndex;
  } else if (targetIndex >= 0) {
    messagesToShow = history.slice(0, targetIndex + 1);
    displayTargetIndex = targetIndex;
  }

  return messagesToShow.map((msg, idx) => {
    const role = msg.role === 'user' ? 'User' : 'Assistant';
    const isTarget = msg.role === 'user' && idx === displayTargetIndex;
    const content = msg.content || msg.text || '';
    return `${role}${isTarget ? ' (Coach Responded To This)' : ''}:\n${content}`;
  }).join('\n\n');
};

/**
 * Format full evaluation for copying
 */
export const formatEvaluationForCopy = (result, score) => {
  const parts = [];
  
  parts.push('=== EVALUATION DETAILS ===');
  parts.push(`Case #${result.case_index}`);
  parts.push(`Category: ${result.case_metadata?.category || 'N/A'}`);
  if (result.case_metadata?.expected_quality) {
    parts.push(`Expected Quality: ${result.case_metadata.expected_quality}`);
  }
  if (result.prompt_type) {
    parts.push(`Prompt Type: ${result.prompt_type}`);
  }
  parts.push(`Overall Score: ${(score * 100).toFixed(1)}%`);
  parts.push(`Correctness: ${result.evaluation?.correctness != null ? ((result.evaluation.correctness * 100).toFixed(1) + '%') : 'N/A'}`);
  parts.push(`Relevance: ${result.evaluation?.relevance != null ? ((result.evaluation.relevance * 100).toFixed(1) + '%') : 'N/A'}`);
  parts.push(`Guidelines Adherence: ${result.evaluation?.guidelines_adherence != null ? ((result.evaluation.guidelines_adherence * 100).toFixed(1) + '%') : 'N/A'}`);
  parts.push('');
  
  const conversationHistory = formatConversationHistoryForCopy(result.history, result.responded_to_index);
  if (conversationHistory) {
    parts.push('=== CONVERSATION HISTORY ===');
    parts.push(conversationHistory);
    parts.push('');
  }
  
  parts.push('=== COACH RESPONSE ===');
  parts.push(result.coach_response || 'No response available');
  parts.push('');
  
  if (result.evaluation?.issues && result.evaluation.issues.length > 0) {
    parts.push('=== ISSUES FOUND BY JUDGE ===');
    result.evaluation.issues.forEach((issue, idx) => {
      parts.push(`${idx + 1}. ${issue}`);
    });
    parts.push('');
  }
  
  if (result.evaluation?.reasoning) {
    parts.push('=== JUDGE REASONING ===');
    parts.push(result.evaluation.reasoning);
  }
  
  return parts.join('\n');
};

